import { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import { AppDispatch } from "@/app/store";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { trackNewsFiltersChanged } from "@/modules/hub/features/News/analytics/newsAnalytics";
import {
  fetchResources,
  fetchMoreResources,
  setResources,
  setPage,
  setHasMore,
  setScrollPosition,
  setLastFetchParams,
  setSearchInput,
  setSelectedTrades,
  setIsRestoringState,
  clearFilters,
} from "@/modules/hub/features/ResourceLibrary/slices/resourceLibrarySlice";
import { getResourceLibraryMetadataAction } from "@/modules/hub/features/ResourceLibrary/slices/resourceLibraryMetadataSlice";
import { z } from "zod";
import { Trade } from "@/modules/hub/features/ResourceLibrary/api/getResourceLibratyMetadata/types";

const NewsFilterFormSchema = z.object({
  searchTerm: z.string().optional(),
  trades: z.array(z.custom<Trade>()).optional(),
});

type NewsFilterFormData = z.infer<typeof NewsFilterFormSchema>;

export function useNews() {
  const dispatch = useDispatch<AppDispatch>();

  const {
    resources,
    page,
    hasMore,
    lastFetchParams,
    scrollPosition,
    searchInput,
    selectedTrades,
  } = useSelector((state: RootState) => state.resourceLibrary);

  const { metadata, loadingGet } = useSelector(
    (state: RootState) => state.resourceLibraryMetadata,
  );

  const form = useForm<NewsFilterFormData>({
    resolver: zodResolver(NewsFilterFormSchema),
    defaultValues: {
      searchTerm: searchInput || "",
      trades: selectedTrades || [],
    },
    mode: "onChange",
  });

  const debouncedSearchTerm = useDebouncedValue(
    form.watch("searchTerm") ?? "",
    500,
  );
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const hasTrackedInitialFilters = useRef(false);

  useEffect(() => {
    const subscription = form.watch((values) => {
      dispatch(setSearchInput(values.searchTerm || ""));
      dispatch(
        setSelectedTrades((values.trades ?? []).filter((t): t is Trade => !!t)),
      );
    });
    return () => subscription.unsubscribe();
  }, [form, dispatch]);

  const buildRequestData = useCallback(() => {
    const values = form.getValues();

    const newsType = metadata?.resourceTypes?.find(
      (type) => type.name === "News",
    );

    return {
      searchTerm: debouncedSearchTerm?.trim() || undefined,
      tradeIds: values.trades?.map((t) => t.id),
      resourceTypeIds: newsType ? [newsType.id] : undefined,
      sortBy: "publishStartDate",
      sortOrder: "desc",
      page: 1,
      pageSize: 10,
    };
  }, [form, debouncedSearchTerm, metadata]);

  const isStateRestorable = useCallback(() => {
    if (!lastFetchParams) return false;

    const getFiltersWithoutPagination = (params: Record<string, unknown>) => {
      const paginationKeys = ["page", "pageSize"];
      return Object.fromEntries(
        Object.entries(params).filter(([key]) => !paginationKeys.includes(key)),
      );
    };

    const currentFilters = getFiltersWithoutPagination(buildRequestData());
    const previousFilters = getFiltersWithoutPagination(lastFetchParams);

    return JSON.stringify(currentFilters) === JSON.stringify(previousFilters);
  }, [lastFetchParams, buildRequestData]);

  useEffect(() => {
    dispatch(getResourceLibraryMetadataAction());
  }, [dispatch]);

  useEffect(() => {
    if (!isInitialLoading && scrollPosition > 0) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPosition, behavior: "auto" });
      });
    }
  }, [isInitialLoading, scrollPosition]);

  useEffect(() => {
    const saveScroll = () => {
      dispatch(setScrollPosition(window.scrollY));
    };
    window.addEventListener("beforeunload", saveScroll);
    window.addEventListener("pagehide", saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener("beforeunload", saveScroll);
      window.removeEventListener("pagehide", saveScroll);
    };
  }, [dispatch]);

  useEffect(() => {
    const load = async () => {
      if (!metadata) {
        return;
      }

      if (resources.length > 0 && lastFetchParams && isStateRestorable()) {
        dispatch(setIsRestoringState(true));
        setIsInitialLoading(false);
        return;
      }

      const request = buildRequestData();
      setLoading(true);
      dispatch(setResources([]));
      dispatch(setPage(1));
      dispatch(setHasMore(true));

      await dispatch(fetchResources(request));

      dispatch(setLastFetchParams(request));
      setLoading(false);
      setIsInitialLoading(false);
    };
    load();
  }, [metadata]);

  useEffect(() => {
    if (isInitialLoading || !metadata) return;
    const request = buildRequestData();
    setLoading(true);
    dispatch(setResources([]));
    dispatch(setPage(1));
    dispatch(setHasMore(true));
    dispatch(fetchResources(request));
    dispatch(setLastFetchParams(request));
    setLoading(false);
  }, [debouncedSearchTerm, form.watch("trades")]);

  useEffect(() => {
    if (isInitialLoading) return;

    if (!hasTrackedInitialFilters.current) {
      hasTrackedInitialFilters.current = true;
      return;
    }

    trackNewsFiltersChanged();
  }, [debouncedSearchTerm, form.watch("trades"), isInitialLoading]);

  const fetchMore = useCallback(() => {
    const nextPage = page + 1;
    const request = { ...buildRequestData(), page: nextPage };
    dispatch(setPage(nextPage));
    dispatch(fetchMoreResources(request));
  }, [page, dispatch, buildRequestData]);

  const handleClearFilters = () => {
    dispatch(clearFilters());
    form.reset({
      searchTerm: "",
      trades: [],
    });
  };

  return {
    form,
    page,
    resources,
    hasMore,
    loading,
    isInitialLoading,
    fetchMore,
    handleClearFilters,
    sidebarMetadata: metadata,
    isMetadataLoading: loadingGet,
  };
}
